import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useMediaMatches, useBreakpoint, useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { useSentrySpanMarker, pendo } from '@frontend/tracking';
import { useRouter } from '@frontend/weave-router';
import { useOnClickOutside } from '@frontend/design-system';
import { Nav } from '../nav';
import { NavItem } from '../nav/primitives/nav-item';
import { useNav } from '../nav/providers/nav-provider';
import { usePrimaryNavContent } from '../nav-content';
import { GlobalNavNav } from './global-nav-nav';

export const FULL_NAV_WIDTH = 267;

interface INavContext {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  hamburgerButtonRef?: React.RefObject<HTMLButtonElement>;
}

const NavContext = createContext<INavContext>({ open: false, setOpen: () => {}, hamburgerButtonRef: undefined });

export const NavContextProvider = ({ children }: { children: ReactNode }) => {
  const open = useNav((state) => state.open);
  const setOpen = useNav((state) => state.setOpen);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);

  const ctxVal = useMemo(
    () => ({
      open,
      setOpen,
      hamburgerButtonRef,
    }),
    [open, setOpen]
  );

  return <NavContext.Provider value={ctxVal}>{children}</NavContext.Provider>;
};

export const useNavTrigger = () => {
  const context = useContext(NavContext);
  return context;
};

export const GlobalNav = () => {
  useSentrySpanMarker('GlobalNav mounted');
  const navItems = usePrimaryNavContent();
  const { setOpen, hamburgerButtonRef } = useNavTrigger();
  const insideRef = useRef<HTMLDivElement>(null);
  const { matches } = useMediaMatches();
  const breakpoint = useBreakpoint();
  const prevBreakpoint = useRef<typeof breakpoint>();
  useEffect(() => {
    //sends breakpoint event on first breakpoint calculation
    if (!prevBreakpoint.current) {
      pendo.track(`breakpoint-set.${breakpoint}`);
    } else if (prevBreakpoint.current !== breakpoint) {
      //sends breakpoint event whenever breakpoint changes
      pendo.track(`breakpoint-change.${breakpoint}`);
    }
    prevBreakpoint.current = breakpoint;
  }, [breakpoint]);

  const closeOnClickOutside = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useOnClickOutside({
    ref: insideRef,
    handler: closeOnClickOutside,
    active: matches.useLargeMax(),
    captureClick: false,
    ignoreConditions: [() => document.getElementById('pendo-resource-center-container') !== null],
    ...(hamburgerButtonRef && { ignoreRefs: [hamburgerButtonRef] }),
  });

  return (
    <>
      <PanelEngineAutoClose />
      <div className='hideforprint' style={{ height: '100%' }} data-testid='global-nav'>
        <Nav>
          {navItems.map((item) => (
            <NavItem item={item} key={item.root} />
          ))}
        </Nav>
      </div>
    </>
  );
};

GlobalNav.Nav = GlobalNavNav;

const PanelEngineAutoClose = () => {
  const router = useRouter();
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { setOpen } = useNavTrigger();

  useEffect(() => {
    const unsubscribe = router.subscribe('onLoad', () => {
      if (isXSmall) {
        setOpen(false);
      }
    });

    return unsubscribe;
  }, [router, isXSmall, setOpen]);

  return null;
};
